import * as _ from "lodash"
import { IdsAttribute, IdsMetric, IdsNode, IdsRow, IMetricDataSet } from "../PA.MetricDataSet.Core/PA.MetricDataSet.IF";
import {
    IHighChartsAxisCommon,
    IHighChartsOptions
} from "../../client-ui5/PA-Controls/Visualization/HighChartsMetaData";
import { DataSetUtils } from "../PA.MetricDataSet.Core/DataSetUtils";
import { NumberUtils } from "../PA.Core/Basic/Numbers"
import { electricConstantDependencies } from "mathjs"
import { Index } from "../PA.Core/Basic/Index";


export class HighChartsMetaDataUtils {

    static setupAxisNumericFormatter(dataSet: IMetricDataSet, axis: IHighChartsAxisCommon, currencyCd?: string) {
        if (_.isEmpty(dataSet) || _.isEmpty(axis))
            return
        if (_.isUndefined(axis.labels))
            axis.labels = {}

        // Don't overwrite and existing formatting
        if (_.isString(axis.labels.format) || _.isFunction(axis.labels.formatter))
            return

        if (!_.isEmpty(axis.labels.PA_numberFormat)) {
            axis.labels.formatter = function() {
                const value = this.value
                if (axis.labels.PA_numberFormat.indexOf("%")>-1) {
                    return ((axis.labels.PA_useSymbols && !_.isEmpty(currencyCd)) ? currencyCd : "") + NumberUtils.msformat(axis.labels.PA_numberFormat, value)
                } else {
                    return ((axis.labels.PA_useSymbols && !_.isEmpty(currencyCd)) ? currencyCd : "") + NumberUtils.d3format(",.2~s", value);
                }
            }
            return
        }

        let axisAttribute: IdsAttribute = undefined
        let axisMetric: IdsMetric = undefined

        if (!_.isEmpty(axis.PA_metricId))
            axisMetric = dataSet.getMetric(axis.PA_metricId)
        if (!_.isEmpty(axis.PA_attributeId))
            axisAttribute = dataSet.getAttribute(axis.PA_attributeId)

        if (axisMetric) {
            axis.labels.formatter = function() {
                return DataSetUtils.Metric_format(axisMetric, this.value)
            }
        } else if (axisAttribute && (axisAttribute.Type == "number")) {
            axis.labels.formatter = function() {
                return DataSetUtils.Attribute_format(axisAttribute, this.value, {})
            }
        }

    }

    static setupPlotLinesData(dataset: IMetricDataSet, md: IHighChartsOptions, sDataSetId?: string) {
        if (_.isEmpty(dataset) || _.isEmpty(md))
            return

        _.forEach(_.union(md.xAxis || [], md.yAxis || []), (axis) => {
            if (!_.isEmpty(sDataSetId) && (sDataSetId != axis.PA_dataSetId))
                return
            HighChartsMetaDataUtils.setupAxisPlotLinesData(dataset, axis)
        })
    }

    static setupAxisPlotLinesData(dataSet: IMetricDataSet, axis: IHighChartsAxisCommon) {
        if (_.isEmpty(dataSet) || _.isEmpty(axis))
            return
        const metric = dataSet.getMetric(axis.PA_metricId);
        const attrib = dataSet.getAttribute(axis.PA_attributeId);

        if (_.isEmpty(metric) && _.isEmpty(attrib))
            return

        //--
        _.forEach(axis.plotLines, (plotLine) => {
            if ((!plotLine.PA_useAvgValue) && (!plotLine.PA_useAggValue))
                return
            let plotLineValue: number = undefined
            let fmt: string = ""


            if (metric) {
                if (plotLine.PA_useAvgValue) {
                    const opts = plotLine.PA_avgOptions || {}
                    plotLineValue = DataSetUtils.Metric_setAvg(dataSet, metric.Id, undefined, dataSet.FilteredRows, opts)
                }
                if(plotLine.PA_useAggValue)
                    plotLineValue = DataSetUtils.Metric_setAggregate(dataSet, metric.Id, undefined, dataSet.FilteredRows)
                fmt = DataSetUtils.Metric_format(metric, plotLineValue)
            }

            if (attrib) {
                const opts = plotLine.PA_avgOptions || {}
                plotLineValue = DataSetUtils.Attribute_setAvg(dataSet, attrib.Id, undefined, dataSet.FilteredRows, opts)
                fmt = DataSetUtils.Attribute_format(attrib, plotLineValue, {})
            }

            if (!_.isUndefined(plotLineValue)) {
                plotLine.value = plotLineValue
                if (_.isString(plotLine.label?.text)) {
                    plotLine.label.text = plotLine.label.text.replace("{value}", fmt)
                }
            }
        })
    }

    static setupAxisCategories(dataSet: IMetricDataSet,
                               dataRows: IdsRow[],
                               axis: IHighChartsAxisCommon,
                               dctLocalNodes: { [attributeId: string]: { [nodeId: number]: IdsNode}}): Index<IdsRow>
    {
        //-- First sort by the axis spec, otherwise, sort by the axis attributes
        if(_.isEmpty(axis?.PA_attributeIds))
            return undefined

        let sortFuncs: ((row: IdsRow) => any)[] = []
        let sortDirection: any[] = []  //  'asc', 'desc'
        let sortSpecs = axis.PA_attributeSort
        if (_.isEmpty(sortSpecs))
            sortSpecs = _.map(axis.PA_attributeIds, (atrId) => {
                return { PA_attributeId: atrId }
            })

        _.forEach(sortSpecs, (spec) => {
            sortDirection.push(spec.descending ? "desc" : "asc")

            let sortAtr: IdsAttribute
            let sortMtr: IdsMetric

            if (!_.isEmpty(spec.PA_metricId))
                sortMtr = dataSet.getMetric(spec.PA_metricId)

            if (!_.isEmpty(spec.PA_attributeId))
                sortAtr = dataSet.getAttribute(spec.PA_attributeId)

            if (sortAtr) {
                let dctNodes: { [nodeId: number]: IdsNode } = {}
                if (sortAtr.Type == "node")
                    dctNodes = dctLocalNodes[sortAtr.Id] || {}

                switch (sortAtr.Type) {
                    case "sapdate":
                    case "date":
                    case "number":
                    case "string":
                        sortFuncs.push((row: IdsRow) => dataSet.getRowAttribute(row, sortAtr.Id))
                        break
                    case "node":
                        sortFuncs.push((row: IdsRow) => DataSetUtils.Attribute_format(sortAtr, dataSet.getRowAttribute(row, sortAtr.Id), dctNodes))
                        break
                    default:
                        sortFuncs.push((row: IdsRow) => 1)
                }

            } else if (sortMtr) {
                sortFuncs.push((row: IdsRow) => dataSet.getRowMetric(row, sortMtr.Id))
            } else {
                sortFuncs.push((row: IdsRow) => 1)
            }
        })

        let orderedPlotRows = _.orderBy(dataRows, sortFuncs, sortDirection)

        // Create Index for xAxis values
        let indexers: ((row: IdsRow) => string)[] = [];

        for(let axisAttrId of axis.PA_attributeIds) {
            const ds = dataSet
            const atr = ds.getAttribute(axisAttrId)
            const attrId = atr.Id;

            indexers.push((row: IdsRow) => {
                let atrV = ds.getRowAttribute(row, attrId);
                return DataSetUtils.Attribute_getIndexingValue(atr, atrV);
            });
        }

        let axisIndex = new Index(indexers, true);
        _.forEach(orderedPlotRows, (row) => {
            axisIndex.add(row);
        });

        //--
        // Set xAxis.categories
        let sep = axis.labels?.PA_attributeSep || ", "
        let axisLabelNames: string[][] = []
        let labelAttrIds = axis.labels?.PA_attributeIds
        if (_.isEmpty(labelAttrIds) || !_.isArray(labelAttrIds)) {
            labelAttrIds = axis.PA_attributeIds
        }

        axisIndex.forEachAddOrder((dataRows: IdsRow[], arrKeys) => {
            const arrLabelName: string[] = []
            const firstRow = _.first(dataRows)
            for (let atrId of labelAttrIds) {
                let atr = dataSet.getAttribute(atrId)
                if (!atr)
                    continue

                let dctNodes: { [nodeId: number]: IdsNode } = {}
                if (atr.Type == "node")
                    dctNodes = dctLocalNodes[atr.Id] || {}


                let labelAtrName = DataSetUtils.Attribute_format(atr, dataSet.getRowAttribute(firstRow, atr.Id), dctNodes)
                arrLabelName.push(labelAtrName)
            }
            axisLabelNames.push(arrLabelName)
        })

        if(axis.labels?.PA_attributeGroupLabels && (labelAttrIds.length > 1) && (axisLabelNames.length > 1)) {
            let lastColIdx = labelAttrIds.length - 1

            if(axis.labels?.PA_attributeGroupLabelsInReverseOrder){
                // Reverse case
                let prevLabels = _.clone(axisLabelNames[axisLabelNames.length-1])
                for(let rowIdx = axisLabelNames.length - 2; rowIdx >= 0 ; rowIdx--) {
                    let curRowLabels = _.clone(axisLabelNames[rowIdx])
                    for(let colIdx = 0; colIdx < lastColIdx; colIdx++) {
                        if(prevLabels[colIdx] == axisLabelNames[rowIdx][colIdx])
                            axisLabelNames[rowIdx][colIdx] = ""
                        else
                            break;
                    }
                    prevLabels = curRowLabels
                }

            } else {
                let prevLabels = _.clone(axisLabelNames[0])
                for(let rowIdx = 1; rowIdx < axisLabelNames.length; rowIdx++) {
                    let curRowLabels = _.clone(axisLabelNames[rowIdx])
                    for(let colIdx = 0; colIdx < lastColIdx; colIdx++) {
                        if(prevLabels[colIdx] == axisLabelNames[rowIdx][colIdx])
                            axisLabelNames[rowIdx][colIdx] = ""
                        else
                            break;
                    }
                    prevLabels = curRowLabels
                }
            }
        }

        axis.categories = _.map(axisLabelNames, (arr) => _.filter(arr, (lbl) => !_.isEmpty(lbl) ).join(sep))

        return axisIndex
    }

    static toDate(dt: Date): string {
        if(!_.isDate(dt))
            return ''
        let month = '' + (dt.getMonth() + 1),
            day = '' + dt.getDate(),
            year = dt.getFullYear();

        if (month.length < 2)
            month = '0' + month;
        if (day.length < 2)
            day = '0' + day;

        return [year, month, day].join('-');

    }
}